import { useContext, useState } from "react";
import { SidebarContext } from "../../context/sidebarContext";
import { PanelLeft, User2, LogOut, Settings, Calendar } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useUser } from "../../context/userContext";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
import { logout } from "../../slices/authSlice";
import { apiSlice } from "../../slices/apiSlice";
import { calendarApiSlice } from "../../slices/calendarApiSlice";
import { leaveApiSlice } from "../../slices/leaveApiSlice";
import { overtimeApiSlice } from "../../slices/overtimeApiSlice";
import { attendanceApiSlice } from "../../slices/attendanceApiSlice";
import { createSelector } from "reselect";
import Avatar from "../Avatar";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const DashboardHeader = ({ navigationLinks = [] }) => {
  const { toggleSidebar } = useContext(SidebarContext);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email, // Skip query if email is empty
  });

  // Check if user should have calendar access
  const userRole = userData?.user?.role?.name?.toLowerCase();
  const shouldShowCalendar = !["admin", "super admin"].includes(userRole);

  // Get academy ID for calendar check
  const academyId = userData?.user?.academyId;
  const currentYear = new Date().getFullYear();

  // Check if calendar exists for current year
  const {
    data: calendarData,
    error: calendarError,
    isLoading: isLoadingCalendar,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: currentYear,
    },
    {
      skip: !academyId || !shouldShowCalendar,
    },
  );

  // Navigation links are now passed as prop from DashboardLayout

  const pathArray = location.pathname.split("/");
  const currentPath = pathArray[pathArray.length - 1];

  // Handle home route (empty path)
  const currentPage = navigationLinks?.find((link) => {
    if (currentPath === "" && link.path === "") {
      return true; // Match home page
    }
    return link.path === currentPath;
  });

  // Handle calendar routes specifically
  let title = "Dashboard";
  if (currentPath === "calendar") {
    title = "Calendar";
  } else if (currentPath && currentPath.startsWith("calendar/")) {
    title = "Calendar";
  } else if (currentPage) {
    title = currentPage.title;
  }

  // Fetched profile image
  const profileImage = userData?.user?.image;
  const userName = userData?.user?.name || "User";

  // Handle navigation to account
  const handleAccountClick = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/account`);
    setIsDropdownOpen(false);
  };

  // Handle logout
  const handleLogout = () => {
    // Clear all RTK Query cache
    dispatch(apiSlice.util.resetApiState());
    dispatch(calendarApiSlice.util.resetApiState());
    dispatch(leaveApiSlice.util.resetApiState());
    dispatch(overtimeApiSlice.util.resetApiState());
    dispatch(attendanceApiSlice.util.resetApiState());

    // Clear auth state and all storage
    dispatch(logout());

    // Navigate to login
    navigate("/login");
    setIsDropdownOpen(false);
  };

  // Handle calendar navigation
  const handleCalendarClick = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];

    // Check if calendar exists for current year
    // If there's an error (like 404), no calendar exists
    // If data exists and has a calendar object, calendar exists
    const hasCalendar = (() => {
      // If still loading, we can't determine yet - default to false to go to overview
      if (isLoadingCalendar) {
        return false;
      }

      // If there's an error (404 or similar), no calendar exists
      if (calendarError) {
        const status = calendarError?.status || calendarError?.originalStatus;
        if (
          status === 404 ||
          status === "FETCH_ERROR" ||
          status === "PARSING_ERROR"
        ) {
          return false;
        }
      }

      // If no data at all, no calendar exists
      if (!calendarData) {
        return false;
      }

      // Check if calendar object exists in the data and is not null/undefined
      // Also check if it has an ID (a valid calendar should have an ID)
      const calendar = calendarData?.calendar;
      return calendar !== null && calendar !== undefined && calendar?.id;
    })();

    if (hasCalendar) {
      // If calendar exists, navigate to that year's calendar
      navigate(`/${roleBasePath}/calendar/${currentYear}`);
    } else {
      // If no calendar exists, navigate to calendar overview page
      navigate(`/${roleBasePath}/calendar`);
    }
  };

  // Handle mouse enter with no delay for showing
  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsDropdownOpen(true);
  };

  // Handle mouse leave with a small delay for hiding
  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150); // 150ms delay to allow moving to dropdown
    setHoverTimeout(timeout);
  };

  return (
    <header className="sticky top-0 w-full bg-white border-b-0 z-50">
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 h-14 sm:h-15 md:h-16">
        {/* Left Section - Menu & Title */}
        <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full hover:bg-zinc-100 transition-colors duration-200 flex-shrink-0"
            onClick={toggleSidebar}
          >
            <PanelLeft className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-700" />
          </button>

          <h1 className="text-sm sm:text-base md:text-lg text-zinc-900 font-semibold truncate">
            {title}
          </h1>
        </div>

        {/* Right Section - Calendar & Profile */}
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {/* Calendar Icon - Only show for non-admin users */}
          {shouldShowCalendar && (
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              onClick={handleCalendarClick}
              title="Calendar"
            >
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
            </button>
          )}

          {/* Profile with Dropdown */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Profile Picture - Make it tappable on mobile */}
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center cursor-pointer focus:outline-none"
              aria-label="User menu"
            >
              <Avatar
                src={profileImage}
                alt={userName}
                size={32}
                className="hover:opacity-90 transition-all duration-200 w-8 h-8 sm:w-9 sm:h-9"
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <>
                {/* Backdrop for mobile - close on tap outside */}
                <div
                  className="fixed inset-0 z-40 md:hidden"
                  onClick={() => setIsDropdownOpen(false)}
                  aria-hidden="true"
                />

                {/* Dropdown Content */}
                <div className="absolute right-0 mt-2 w-64 sm:w-72 md:w-64 bg-white rounded-lg shadow-xl overflow-hidden border border-zinc-200 z-[9999] max-w-[calc(100vw-1rem)]">
                  {/* User Info - Now clickable */}
                  <button
                    onClick={handleAccountClick}
                    className="flex items-center w-full px-3 py-3 border-b border-zinc-200 hover:bg-zinc-100 transition-colors duration-150 text-left"
                  >
                    <div className="flex items-center mr-3 flex-shrink-0">
                      <Avatar
                        src={profileImage}
                        alt={userName}
                        size={40}
                        className="w-10 h-10"
                      />
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-sm font-semibold text-zinc-900 truncate">
                        {userName}
                      </p>
                      <p className="text-xs text-zinc-500 truncate">{email}</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize mt-1.5">
                        {userData?.user?.role?.name || "User"}
                      </span>
                    </div>
                  </button>

                  {/* Menu Items */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full rounded-none px-3 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 font-medium"
                  >
                    <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
